import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import rendezvousLogo from './Assets/rendezvousLogo.png';
import dayjs from 'dayjs';
import TicketModal from './Components/TicketModal';
import TicketEmail from './Components/TicketEmail';
import { AccessTime, AccountCircle, ConfirmationNumber } from '@mui/icons-material';
import TicketRulesModal from './Components/TicketRulesModal';

const Tickets = () => {
  const [searchParams] = useSearchParams();
  const submitter = searchParams.get('submitter') || '';
  const [selectedBooth, setSelectedBooth] = useState(searchParams.get('booth') || null);
  const [tickets, setTickets] = useState(null);
  const [boothNumbers, setBoothNumbers] = useState([]);
  const [ticketRules, setTicketRules] = useState([]);
  const [ticketModalOpen, setTicketModalOpen] = useState(false);
  const [ticketRulesModalOpen, setTicketRulesModalOpen] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [highlightFirstTicket, setHighlightFirstTicket] = useState(false);
  const [showFollowUpOnly, setShowFollowUpOnly] = useState(false);

  const getTickets = () => {
    setTickets(null);
    axios
      .get('/rotr/tickets')
      .then((response) => {
        setTickets(response.data);
      })
      .catch(() => {
        setTickets('error');
      });
  };

  const getBoothNumbers = () => {
    setBoothNumbers([]);
    axios
      .get('/rotr/boothNumbers')
      .then((response) => {
        setBoothNumbers(response.data?.map((booth) => booth.booth_number) || []);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const getTicketRules = () => {
    setTicketRules([]);
    axios
      .get('/rotr/ticketRules')
      .then((response) => {
        setTicketRules(response.data);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    getTickets();
    getBoothNumbers();
    getTicketRules();
  }, []);

  const saveTicket = (ticketDetails) => {
    setOpenSnackbar(false);
    Object.keys(ticketDetails).forEach((key) => {
      if (typeof ticketDetails[key] === 'string') {
        ticketDetails[key] = ticketDetails[key].trim();
      }
    });
    return new Promise((resolve, reject) => {
      axios
        .post('/rotr/ticket', {
          submittedBy: ticketDetails.createdBy,
          boothNumber: ticketDetails.boothNumber,
          issue: ticketDetails.issue,
          actionTaken: ticketDetails.actionTaken,
          notes: ticketDetails.notes,
        })
        .then((response) => {
          TicketEmail({
            submittedBy: ticketDetails.createdBy,
            submittedOn: dayjs(response.data[2].submitted_on).format('MM/DD/YYYY h:mm A'),
            boothNumber: ticketDetails.boothNumber,
            issue: ticketDetails.issue,
            actionTaken: ticketDetails.actionTaken,
            notes: ticketDetails.notes,
            link: `https://www.rendezvousonthered.com/tickets?booth=${encodeURIComponent(
              ticketDetails.boothNumber
            )}`,
          }).catch((err) => {
            console.error(err);
          });

          setSnackbarMessage('Successfully submitted ticket!');
          setSnackbarSeverity('success');
          setOpenSnackbar(true);
          setHighlightFirstTicket(true);
          getTickets();
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  const filteredTickets =
    tickets && Array.isArray(tickets)
      ? tickets.filter((ticket) => {
          const matchesBooth = boothNumbers?.includes(selectedBooth)
            ? ticket.booth === selectedBooth
            : true;
          const matchesFollowUp = showFollowUpOnly
            ? ticket.action_taken?.toLowerCase() === 'follow up required'
            : true;
          return matchesBooth && matchesFollowUp;
        })
      : [];

  return (
    <>
      <Box
        sx={{
          my: 2,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <Box sx={{ display: 'flex', maxWidth: '50%', width: '500px', justifyContent: 'center' }}>
          <img src={rendezvousLogo} alt='RotR Logo' style={{ maxWidth: '100%', height: 'auto' }} />
        </Box>
        <Stack direction='row' spacing={2}>
          {submitter && (
            <Button
              variant='contained'
              color='rendezvousRed'
              onClick={() => setTicketModalOpen(true)}
            >
              New Ticket
            </Button>
          )}
          <Button variant='contained' color='primary' onClick={() => setTicketRulesModalOpen(true)}>
            View Rules
          </Button>
        </Stack>
        {tickets === null ? (
          <CircularProgress />
        ) : tickets === 'error' ? (
          <Typography variant='h5' sx={{ fontStyle: 'italic', color: '#222' }}>
            Error loading tickets. Please refresh and try again, or contact an administrator if this
            continues.
          </Typography>
        ) : tickets?.length === 0 ? (
          <Typography variant='h5' sx={{ fontStyle: 'italic', color: '#222' }}>
            No tickets found.
          </Typography>
        ) : (
          <Box sx={{ maxWidth: '1600px', width: 'calc(100% - 32px)' }}>
            <Box
              sx={{
                mb: 3,
                maxWidth: '400px',
                mx: 'auto',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Autocomplete
                options={boothNumbers || []}
                getOptionLabel={(option) => option}
                value={boothNumbers?.includes(selectedBooth) ? selectedBooth : null}
                onChange={(event, newValue) => {
                  setSelectedBooth(newValue);
                }}
                renderInput={(params) => <TextField {...params} label='Filter by Booth #' />}
                sx={{ width: '100%' }}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showFollowUpOnly}
                    onChange={(e) => setShowFollowUpOnly(e.target.checked)}
                    color='primary'
                  />
                }
                label={
                  <Typography variant='body2' sx={{ color: 'text.secondary', fontWeight: 500 }}>
                    Only show "Follow Up Required"
                  </Typography>
                }
                sx={{ mt: 1 }}
              />
            </Box>

            {filteredTickets.length > 0 ? (
              <Grid container spacing={3} sx={{ justifyContent: 'center' }}>
                {filteredTickets.map((ticket, index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      sx={{
                        position: 'relative',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        border: '1px solid',
                        borderColor: 'grey.300',
                        transition: 'box-shadow 0.3s, border-color 0.3s',
                        '&:hover': {
                          boxShadow: 3,
                          borderColor: 'primary.main',
                        },
                        ...(highlightFirstTicket && index === 0 && !selectedBooth
                          ? {
                              borderWidth: '2px',
                              borderColor: 'rendezvousRed.main',
                            }
                          : {}),
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography
                          variant='h5'
                          component='div'
                          sx={{
                            position: 'absolute',
                            top: 16,
                            right: 16,
                            fontWeight: 'bold',
                            color: 'grey.500',
                            zIndex: 1,
                          }}
                        >
                          #{ticket.booth}
                        </Typography>

                        {highlightFirstTicket && index === 0 && !selectedBooth && (
                          <Chip
                            label='New Ticket'
                            color='rendezvousRed'
                            sx={{ mb: 2, fontWeight: 'bold' }}
                          />
                        )}

                        <Stack direction='column' spacing={0.5} sx={{ mb: 2, pr: '75px' }}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                            color='text.secondary'
                          >
                            <ConfirmationNumber sx={{ fontSize: 16 }} />
                            <Typography variant='body2'>{ticket.ticket_id}</Typography>
                          </Stack>
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                            color='text.secondary'
                          >
                            <AccountCircle sx={{ fontSize: 16 }} />
                            <Typography variant='body2'>{ticket.submitted_by}</Typography>
                          </Stack>
                          <Stack
                            direction='row'
                            alignItems='center'
                            spacing={1}
                            color='text.secondary'
                          >
                            <AccessTime sx={{ fontSize: 16 }} />
                            <Typography variant='body2'>
                              {dayjs(ticket.submitted_on).format('MM/DD/YYYY h:mm A')}
                            </Typography>
                          </Stack>
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Stack direction='column' spacing={2}>
                          <Box>
                            <Typography
                              variant='subtitle1'
                              sx={{ fontWeight: 'bold' }}
                              gutterBottom
                            >
                              Issue
                            </Typography>
                            <Typography variant='body1' color='text.secondary'>
                              {ticket.issue}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              variant='subtitle1'
                              sx={{ fontWeight: 'bold' }}
                              gutterBottom
                            >
                              Action Taken
                            </Typography>
                            <Typography variant='body1' color='text.secondary'>
                              {ticket.action_taken}
                            </Typography>
                          </Box>
                          {ticket.notes && (
                            <Box>
                              <Typography
                                variant='subtitle1'
                                sx={{ fontWeight: 'bold' }}
                                gutterBottom
                              >
                                Notes
                              </Typography>
                              <Typography variant='body1' color='text.secondary'>
                                {ticket.notes}
                              </Typography>
                            </Box>
                          )}
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Typography variant='h6' sx={{ color: '#222', textAlign: 'center' }}>
                No tickets found for the selected booth.
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <TicketModal
        open={ticketModalOpen}
        onClose={() => setTicketModalOpen(false)}
        submitter={submitter}
        onSave={saveTicket}
        boothNumbers={boothNumbers}
      />
      <TicketRulesModal
        open={ticketRulesModalOpen}
        onClose={() => setTicketRulesModalOpen(false)}
        ticketRules={ticketRules}
      />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default Tickets;
